import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Loader2, Sparkles, ArrowLeft, Bot, User as UserIcon, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function CoachChat() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [locked, setLocked] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth");
      return;
    }
    api
      .get(`/plans/${id}/chat`)
      .then((r) => setMessages(r.data.messages || []))
      .catch((err) => {
        if (err?.response?.status === 402) setLocked(true);
        else toast.error(err?.response?.data?.detail || "Could not load chat");
      })
      .finally(() => setLoadingHistory(false));
  }, [id, user, authLoading, navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    // Optimistic user bubble
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post(`/plans/${id}/chat`, { message: msg });
      // Replace optimistic + add real pair from server
      setMessages((prev) => {
        const noTmp = prev.filter((m) => !String(m.id).startsWith("tmp-"));
        return [...noTmp, ...data.messages];
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
      setMessages((prev) => prev.filter((m) => !String(m.id).startsWith("tmp-")));
    } finally {
      setSending(false);
    }
  };

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center">
        <Sparkles className="w-10 h-10 mx-auto text-[#FF3333]" />
        <h1 className="font-heading uppercase text-4xl mt-4">Coach is locked</h1>
        <p className="font-sub text-white/60 mt-2">Unlock your plan to chat with FORGE-AI 24/7.</p>
        <Link to={`/plan/${id}`} className="btn-forged-red mt-8 inline-flex">Unlock Plan</Link>
      </div>
    );
  }

  const samples = [
    "Swap my deadlift day for something easier on my lower back",
    "I hate eggs — replace breakfast",
    "Bump calories +200 for a bulk",
    "Give me a 20-minute fat-burn finisher",
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 flex flex-col h-[calc(100vh-4rem)]" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to={`/plan/${id}`} className="font-mono text-[10px] tracking-widest text-white/40 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> BACK TO PLAN
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#00FF66] animate-pulse" />
          <div className="font-mono text-[10px] tracking-widest text-[#00FF66]">FORGE-AI ONLINE</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 brutal-border-red bg-[#0a0a0a] flex items-center justify-center">
          <Bot className="w-6 h-6 text-[#FF3333]" />
        </div>
        <div>
          <h1 className="font-heading uppercase text-2xl leading-none">FORGE-AI Coach</h1>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mt-1">YOUR PLAN · YOUR RULES · 24/7</div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto brutal-border bg-[#0a0a0a] p-4 md:p-6 space-y-4">
        {loadingHistory ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF3333]" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="font-jp text-5xl text-white/10">力</div>
            <div className="font-heading uppercase text-xl mt-4">Ask me anything</div>
            <div className="font-sub text-white/50 mt-2 max-w-md mx-auto">
              I have your full plan loaded. Adjust workouts, swap meals, ask for form cues, or get fired up.
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
              {samples.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  data-testid={`chat-sample-${s.slice(0, 12)}`}
                  className="text-left p-3 brutal-border hover:border-[#FF3333]/40 font-sub text-sm text-white/70 hover:text-white transition-colors"
                >
                  → {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 flex-shrink-0 border border-[#FF3333]/40 flex items-center justify-center bg-black">
                  <Bot className="w-4 h-4 text-[#FF3333]" />
                </div>
              )}
              <div className={`max-w-[80%] p-3 ${m.role === "user" ? "bg-[#00E5FF]/10 border border-[#00E5FF]/30" : "bg-black border border-white/10"}`}>
                <div className="font-sub text-white/90 text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {m.patch_note && (
                  <div className="mt-3 flex items-start gap-2 text-[#FFB800] border-t border-white/5 pt-2">
                    <Wand2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <div className="font-mono text-[10px] tracking-wider leading-snug">PLAN CHANGE: {m.patch_note}</div>
                  </div>
                )}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 flex-shrink-0 border border-[#00E5FF]/40 flex items-center justify-center bg-black">
                  <UserIcon className="w-4 h-4 text-[#00E5FF]" />
                </div>
              )}
            </div>
          ))
        )}
        {sending && (
          <div className="flex items-center gap-2 text-white/50 font-mono text-xs">
            <Loader2 className="w-3 h-3 animate-spin" /> FORGE-AI is typing...
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form onSubmit={send} className="mt-4 flex gap-2" data-testid="chat-composer">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message to your AI coach..."
          data-testid="chat-input"
          className="flex-1 bg-[#0a0a0a] border border-white/15 focus:border-[#FF3333] outline-none px-4 py-3 font-sub text-base transition-colors"
        />
        <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send" className="btn-forged-red disabled:opacity-40">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}
        </button>
      </form>
    </div>
  );
}
